import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Trash2, Check } from "lucide-react";
import { toast } from "sonner";

export function useCategories(open = true) {
  const [cats, setCats] = useState([]);
  const load = () => api.get("/employee-categories").then((r) => setCats(r.data)).catch(() => {});
  useEffect(() => { if (open) load(); }, [open]);
  return [cats, load];
}

export function CategoriesDialog({ open, onOpenChange, onChanged }) {
  const [cats, load] = useCategories(open);
  const [name, setName] = useState("");
  const [edit, setEdit] = useState({});
  const err = (e) => toast.error(formatApiError(e.response?.data?.detail));
  const done = () => { load(); onChanged?.(); };
  const add = async () => { try { await api.post("/employee-categories", { name }); setName(""); done(); } catch (e) { err(e); } };
  const rename = async (c) => { try { await api.put(`/employee-categories/${c.id}`, { name: edit[c.id] }); setEdit({ ...edit, [c.id]: undefined }); done(); } catch (e) { err(e); } };
  const del = async (c) => { if (!window.confirm(`Delete category "${c.name}"? Staff are un-assigned from it.`)) return; try { await api.delete(`/employee-categories/${c.id}`); done(); } catch (e) { err(e); } };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="categories-dialog">
        <DialogHeader><DialogTitle>Staff categories</DialogTitle><DialogDescription>e.g. Van Drivers, Site Leads. Assign staff with the tag button on each row, then target forms and resources at a category.</DialogDescription></DialogHeader>
        <div className="flex gap-2"><Input data-testid="cat-new-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="New category" /><Button data-testid="cat-add" onClick={add} disabled={!name.trim()} className="bg-slate-900 text-white">Add</Button></div>
        <div className="space-y-1.5 max-h-72 overflow-y-auto">
          {cats.map((c) => (
            <div key={c.id} data-testid={`cat-row-${c.id}`} className="flex items-center gap-2 border border-zinc-200 rounded-md px-2 py-1.5">
              {c.builtin ? <span className="flex-1 text-sm">{c.name} <span className="text-[10px] text-zinc-400">(automatic)</span></span>
                : <Input data-testid={`cat-name-${c.id}`} className="h-8 flex-1" value={edit[c.id] ?? c.name} onChange={(e) => setEdit({ ...edit, [c.id]: e.target.value })} />}
              <span className="text-xs text-zinc-400 w-12 text-right">{c.count} staff</span>
              {!c.builtin && edit[c.id] !== undefined && edit[c.id] !== c.name && <button data-testid={`cat-save-${c.id}`} onClick={() => rename(c)} className="text-emerald-600"><Check className="h-4 w-4" /></button>}
              {!c.builtin && <button data-testid={`cat-del-${c.id}`} onClick={() => del(c)} className="text-rose-400 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>}
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function CategoryPicker({ cats, value, onChange, testid = "cat-pick", includeBuiltin = false }) {
  const list = cats.filter((c) => includeBuiltin || !c.builtin);
  if (!list.length) return <div className="text-xs text-zinc-400">No categories yet</div>;
  return (
    <div className="flex flex-wrap gap-1.5">
      {list.map((c) => {
        const on = (value || []).includes(c.name);
        return <button key={c.id} type="button" data-testid={`${testid}-${c.name}`} onClick={() => onChange(on ? value.filter((x) => x !== c.name) : [...(value || []), c.name])}
          className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${on ? "bg-slate-900 text-white border-slate-900" : "bg-white border-zinc-200 hover:border-zinc-400"}`}>{c.name}</button>;
      })}
    </div>
  );
}
